import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';

import { lireMonJournal, type PageMonJournal } from '../api/journal';
import { TableJournal } from '../components/TableJournal';

interface Etat {
  readonly donnees?: PageMonJournal;
  readonly chargement: boolean;
  readonly erreur?: string;
}

/**
 * Le journal du compte, DANS le tableau de bord.
 *
 * Même contrat que l'écran Journal de l'administrateur — filtre par action,
 * pagination, message d'erreur avec « Réessayer » — mais porté sur les traces du
 * compte connecté : le serveur décide ce qu'il a le droit de voir, l'écran
 * n'envoie qu'un filtre et un numéro de page.
 */
export function JournalDuCompte() {
  const [etat, setEtat] = useState<Etat>({ chargement: true });
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);

  const charger = useCallback(
    async (signal: AbortSignal) => {
      setEtat((precedent) => ({ ...precedent, chargement: true, erreur: undefined }));
      const resultat = await lireMonJournal({ ...(action ? { action } : {}), page }, { signal });
      if (resultat.ok && resultat.donnees) {
        setEtat({ donnees: resultat.donnees, chargement: false });
      } else {
        setEtat((precedent) => ({
          ...precedent,
          chargement: false,
          erreur: resultat.message ?? 'Erreur inattendue.',
        }));
      }
    },
    [action, page],
  );

  useEffect(() => {
    const controleur = new AbortController();
    void charger(controleur.signal);
    return () => controleur.abort();
  }, [charger]);

  const donnees = etat.donnees;
  // Le serveur ramène une page hors bornes à la dernière page réelle : l'écran suit.
  useEffect(() => {
    if (!donnees) return;
    if (donnees.pages > 0 && page > donnees.pages) setPage(donnees.pages);
  }, [donnees, page]);

  const actions = donnees?.actions ?? [];

  return (
    <section
      aria-labelledby="titre-journal-compte"
      className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
    >
      <div className="px-6 py-4 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h2 id="titre-journal-compte" className="text-base font-bold text-slate-800">
            Journal de mon compte
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Ce que ce compte a fait, et ce qu’on lui a fait. Rien d’autre : le journal des autres
            comptes reste chez l’administrateur.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label htmlFor="filtre-action-bord" className="sr-only">
            Filtrer le journal par action
          </label>
          <select
            id="filtre-action-bord"
            value={action}
            onChange={(evenement) => {
              setAction(evenement.target.value);
              setPage(1);
            }}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="">Toutes les actions</option>
            {actions.map((entree) => (
              <option key={entree.code} value={entree.code}>
                {entree.libelle}
              </option>
            ))}
          </select>
        </div>
      </div>

      {etat.erreur && (
        <div className="p-6 border-b border-slate-100" role="alert">
          <p className="font-medium text-red-700 mb-1">Journal indisponible</p>
          <p className="text-sm text-slate-600 mb-4">
            {etat.erreur}
            {/* Une erreur de rafraîchissement ne doit pas effacer ce qui était lisible. */}
            {donnees ? ' Les entrées ci-dessous datent de la dernière lecture réussie.' : ''}
          </p>
          <button
            type="button"
            onClick={() => void charger(new AbortController().signal)}
            className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
          >
            Réessayer
          </button>
        </div>
      )}

      {!donnees && etat.chargement && !etat.erreur && (
        <p className="p-6 text-sm text-slate-500">Chargement du journal…</p>
      )}

      {donnees && (
        <>
          <TableJournal
            items={donnees.items}
            chargement={etat.chargement}
            legende="Journal du compte connecté, filtré et paginé par le serveur"
            messageVide={
              action === ''
                ? 'Aucune activité enregistrée pour ce compte.'
                : 'Aucune activité pour cette action.'
            }
          />

          <div className="flex flex-col md:flex-row justify-between items-center gap-3 px-6 py-4 border-t border-slate-100">
            <p className="text-xs text-slate-500">
              {donnees.total === 0
                ? 'Aucune entrée'
                : `${donnees.total} entrée${donnees.total > 1 ? 's' : ''} · page ${donnees.page} sur ${Math.max(donnees.pages, 1)}`}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={donnees.page <= 1}
                onClick={() => setPage((actuelle) => Math.max(1, actuelle - 1))}
                className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40"
              >
                Précédent
              </button>
              <button
                type="button"
                disabled={donnees.pages === 0 || donnees.page >= donnees.pages}
                onClick={() => setPage((actuelle) => actuelle + 1)}
                className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40"
              >
                Suivant
              </button>
              {/* Le serveur dit si ce compte a le droit au journal global. */}
              {donnees.journalComplet && (
                <Link
                  to="/journal"
                  className="ml-2 text-xs text-red-600 font-medium hover:underline"
                >
                  Journal complet
                </Link>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
